/**
 * Drawer с iOS-style wheel picker для выбора даты
 *
 * Shared-компонент: все тексты передаются через пропсы (без useTranslation)
 */

import { useCallback, useState } from 'react';
import Picker from 'react-mobile-picker';
import { Button } from '@/components/ui/button';
import { BaseDrawer } from '../base-drawer';
import type { BaseDrawerProps } from '@/types';

interface DatePickerDrawerProps extends BaseDrawerProps {
  selectedDate: Date | undefined;
  onConfirm: (date: Date | undefined) => void;
  mode?: 'future' | 'past';
  /** Текст заголовка drawer */
  titleText: string;
  /** Текст кнопки подтверждения */
  confirmText: string;
  /** Текст кнопки очистки */
  clearText: string;
  /** Названия месяцев в родительном падеже (12 элементов) */
  monthNames: readonly string[];
}

// Генерация массива годов в зависимости от режима
const getYears = (mode: 'future' | 'past' = 'future'): string[] => {
  const currentYear = new Date().getFullYear();
  if (mode === 'past') {
    // Для даты рождения: 1925-2025 (100 лет назад до текущего года)
    return Array.from({ length: 101 }, (_, i) => (1925 + i).toString());
  }
  // Для событий: 2024-2035 (12 лет вперед)
  return Array.from({ length: 12 }, (_, i) => (currentYear + i).toString());
};

const DAYS = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

export function DatePickerDrawer({ open, onOpenChange, selectedDate, onConfirm, mode = 'future', titleText, confirmText, clearText, monthNames }: DatePickerDrawerProps): JSX.Element {
  // Используем переданные названия месяцев напрямую
  const MONTH_NAMES = monthNames;
  
  // Инициализация значений из selectedDate или дефолтной даты
  const initializeValues = useCallback(() => {
    let date: Date;
    if (selectedDate) {
      date = selectedDate;
    } else {
      // Для режима 'past' (ДР) - дефолт 1 января 2001
      // Для режима 'future' (события) - текущая дата
      date = mode === 'past' ? new Date(2001, 0, 1) : new Date();
    }
    return {
      day: date.getDate().toString(),
      month: MONTH_NAMES[date.getMonth()],
      year: date.getFullYear().toString()
    };
  }, [selectedDate, MONTH_NAMES, mode]);

  const [pickerValue, setPickerValue] = useState(initializeValues);

  // Синхронизация при открытии — через callback вместо useEffect
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setPickerValue(initializeValues());
    }
    onOpenChange(isOpen);
  }, [initializeValues, onOpenChange]);

  const handleConfirm = () => {
    // Преобразуем выбранные значения в Date
    const monthIndex = MONTH_NAMES.indexOf(pickerValue.month);
    const day = parseInt(pickerValue.day);
    const year = parseInt(pickerValue.year);
    
    const newDate = new Date(year, monthIndex, day);
    onConfirm(newDate);
    handleOpenChange(false);
  };

  const handleClear = () => {
    onConfirm(undefined);
    handleOpenChange(false);
  };

  return (
    <BaseDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title={titleText}
      footer={
        <div className="flex gap-3">
          {selectedDate && (
            <Button
              variant="outline"
              onClick={handleClear}
              className="flex-[1]"
            >
              {clearText}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            className={selectedDate ? "flex-[2]" : "w-full"}
          >
            {confirmText}
          </Button>
        </div>
      }
    >
      <div className="pt-4">
        {/* iOS-style Wheel Picker */}
        <div className="relative date-picker-no-mask" data-vaul-no-drag>
          <Picker
            value={pickerValue}
            onChange={setPickerValue}
            wheelMode="normal"
            height={200}
            itemHeight={40}
          >
            <Picker.Column name="day">
              {DAYS.map(day => (
                <Picker.Item key={day} value={day}>
                  {day}
                </Picker.Item>
              ))}
            </Picker.Column>
            
            <Picker.Column name="month">
              {MONTH_NAMES.map(month => (
                <Picker.Item key={month} value={month}>
                  {month}
                </Picker.Item>
              ))}
            </Picker.Column>
            
            <Picker.Column name="year">
              {getYears(mode).map(year => (
                <Picker.Item key={year} value={year}>
                  {year}
                </Picker.Item>
              ))}
            </Picker.Column>
          </Picker>
        </div>
      </div>
      
      {/* Кастомные стили для iOS-look */}
      <style>{`
        .picker-container {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        .picker-column {
          color: var(--foreground);
        }
        
        .picker-item {
          font-size: 20px;
          font-weight: 400;
          color: var(--muted-foreground);
          transition: all 0.2s ease;
        }
        
        .picker-item.picker-item-selected {
          color: var(--foreground);
          font-weight: 600;
        }
        
        .picker-highlight {
          background: linear-gradient(to bottom, 
            color-mix(in srgb, var(--purple-dark) 5%, transparent) 0%, 
            color-mix(in srgb, var(--purple-dark) 8%, transparent) 50%, 
            color-mix(in srgb, var(--purple-dark) 5%, transparent) 100%
          );
          border-top: 1px solid color-mix(in srgb, var(--purple-dark) 10%, transparent);
          border-bottom: 1px solid color-mix(in srgb, var(--purple-dark) 10%, transparent);
        }

        /* Убираем блюр/затухание крайних строк */
        .date-picker-no-mask > div {
          mask-image: none !important;
          -webkit-mask-image: none !important;
        }
      `}</style>
    </BaseDrawer>
  );
}